import { waDigits, whatsAppHref } from "../../catalog/ecomerce/catalogueAudience";
import type { ParcelStage, ParcelView, SalesApiResult, SlipFileType } from "../../../services/studioSalesApi";

/** Parcels are kept only by studio staff; the server refuses anyone else (and a 403 there leaves the page). */
export const PARCEL_ROLES = ["ORGANIZATION_ADMIN", "ORGANIZATION_MANAGER"];

const BILLED = ["INVOICE_GENERATED", "BILLED"];
const APPROVAL_OUT = ["OUT_ON_APPROVAL", "CLOSED"];

/**
 * What the Parcels card may do on this memo: "edit" on a billed sale memo or an approval memo whose pieces went
 * out, "read" on a cancelled sale or approval memo (it still shows the parcels it had), null otherwise
 * (credit notes, drafts, memos not yet billed).
 */
export const parcelMode = (memo: { type?: string; status?: string } | null | undefined): "edit" | "read" | null => {
  if (!memo || !memo.status) return null;
  const type = memo.type || "SALE";
  if (type !== "SALE" && type !== "APPROVAL") return null;
  if (memo.status === "CANCELLED") return "read";
  if (type === "SALE") return BILLED.includes(memo.status) ? "edit" : null;
  return APPROVAL_OUT.includes(memo.status) ? "edit" : null;
};

/** The feature is off on the server (flag, orders feature) or the studio lacks the module: show nothing. */
export const isParcelsOff = (res: SalesApiResult<unknown> | null | undefined) =>
  !!res && !res.success && (res.message === "FEATURE_DISABLED" || res.message === "MODULE_REQUIRED"
    || res.code === "FEATURE_DISABLED" || res.code === "MODULE_REQUIRED");

/** How long after booking a parcel is flagged (the server's default; it sends only the flag). */
export const OVERDUE_DAYS = 15;

type StageInfo = { label: string; glyph: string; tone: "closed" | "ready" | "done" | "bad" };
/** Word plus glyph for every stage: never colour alone. */
export const STAGES: Record<ParcelStage, StageInfo> = {
  BOOKED: { label: "Booked", glyph: "○", tone: "closed" },
  IN_TRANSIT: { label: "In transit", glyph: "→", tone: "ready" },
  OUT_FOR_DELIVERY: { label: "Out for delivery", glyph: "→", tone: "ready" },
  DELIVERED: { label: "Delivered", glyph: "✓", tone: "done" },
  RETURNED: { label: "Returned to sender", glyph: "↩", tone: "bad" },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-11-02" → "2 Nov"; "" when it is not a date. */
export const dayLabel = (iso?: string | null): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return "";
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month}` : "";
};

const msDay = (ms?: number | null) => {
  if (!ms) return "";
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? "" : `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

/** Today on this device as yyyy-MM-dd. */
export const todayIso = (now: Date = new Date()) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

/** Days after booking when "Mark delivered" moves from the ⋯ menu to a button on the parcel (owner, 2026-11-03). */
export const DELIVERED_BUTTON_DAYS = 3;

/** Whether the parcel shows the "Mark delivered" button: not delivered or returned, booked at least 3 days ago. */
export const deliveredButtonDue = (parcel: Pick<ParcelView, "stage" | "bookedOn">, now: Date = new Date()): boolean => {
  if (parcel.stage === "DELIVERED" || parcel.stage === "RETURNED" || !parcel.bookedOn) return false;
  const booked = Date.parse(`${parcel.bookedOn}T00:00:00Z`);
  const today = Date.parse(`${todayIso(now)}T00:00:00Z`);
  if (Number.isNaN(booked) || Number.isNaN(today)) return false;
  return (today - booked) / 86_400_000 >= DELIVERED_BUTTON_DAYS;
};

/** The stage chip's text: "Booked · 2 Nov", "Delivered 7 Nov", "In transit". */
export const stageText = (parcel: Pick<ParcelView, "stage" | "bookedOn" | "deliveredOn">): string => {
  const info = STAGES[parcel.stage] || STAGES.BOOKED;
  if (parcel.stage === "BOOKED") return [info.label, dayLabel(parcel.bookedOn)].filter(Boolean).join(" · ");
  if (parcel.stage === "DELIVERED" && parcel.deliveredOn) return `${info.label} ${dayLabel(parcel.deliveredOn)}`;
  return info.label;
};

/** Where a non-Booked stage came from. Before the courier reader exists, every such stage was set by a person. */
export const stageSourceText = (parcel: Pick<ParcelView, "stage" | "stageSource" | "stageByName" | "stageAt">): string => {
  if (parcel.stage === "BOOKED" || parcel.stageSource !== "MANUAL") return "";
  const who = (parcel.stageByName || "").trim();
  const when = msDay(parcel.stageAt);
  return `marked${who ? ` by ${who}` : ""}${when ? `, ${when}` : ""}`;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const startOfDay = (ms: number) => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };

/** "today", "yesterday", "3 days ago" for a past time on this device's calendar. */
export const daysAgoText = (ms: number, now: Date = new Date()): string => {
  const days = Math.max(0, Math.round((startOfDay(now.getTime()) - startOfDay(ms)) / DAY_MS));
  return days === 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
};

/**
 * Honest automatic status, only for a courier with a status reader switched on: "checked 2 days ago", or after a
 * failed check "Couldn't check · last known In transit, 4 Nov". Null otherwise, so nothing suggests a check that is
 * not happening (rows then rely on "Open courier page").
 */
export const checkLine = (
  parcel: Pick<ParcelView, "autoCheck" | "checkState" | "lastCheckedAt" | "stage" | "stageAt" | "bookedOn" | "deliveredOn">,
  now: Date = new Date(),
): { text: string; failed: boolean } | null => {
  if (!parcel.autoCheck) return null;
  // Delivered or returned: checks have stopped, and an old failed check says nothing about it any more.
  const done = parcel.stage === "DELIVERED" || parcel.stage === "RETURNED";
  if (parcel.checkState === "FAILED" && !done) {
    const info = STAGES[parcel.stage] || STAGES.BOOKED;
    const when = parcel.stage === "BOOKED" ? dayLabel(parcel.bookedOn)
      : parcel.stage === "DELIVERED" && parcel.deliveredOn ? dayLabel(parcel.deliveredOn) : msDay(parcel.stageAt);
    return { text: `Couldn't check · last known ${info.label}${when ? `, ${when}` : ""}`, failed: true };
  }
  if (!parcel.lastCheckedAt) return null;
  return { text: `checked ${daysAgoText(parcel.lastCheckedAt, now)}`, failed: false };
};

/** The quiet Zoho sub-line on a parcel row; null when there is nothing to say (sync off, or no Zoho document). */
export const zohoLine = (parcel: Pick<ParcelView, "zohoSyncState" | "memoType">):
  { text: string; retry: boolean; tone: "done" | "wait" | "bad" } | null => {
  switch (parcel.zohoSyncState) {
    case "SYNCED": return { text: "In Zoho notes ✓", retry: false, tone: "done" };
    case "PENDING": return { text: "Sending to Zoho…", retry: false, tone: "wait" };
    case "FAILED": return { text: "Not in Zoho", retry: true, tone: "bad" };
    default: return null;
  }
};

/** The amber overlay on a parcel the server flagged; "" when not flagged. */
export const overdueText = (parcel: Pick<ParcelView, "overdue" | "courierName" | "overdueDays">): string =>
  parcel.overdue ? `Not delivered after ${parcel.overdueDays || OVERDUE_DAYS} days · check with ${parcel.courierName || "the courier"}` : "";

/** The comparable AWB (as the server keeps it): upper case, spaces and dashes dropped. */
export const normaliseAwb = (typed: string): string => (typed || "").trim().toUpperCase().replace(/[\s-]+/g, "");

/** 4 to 30 letters and digits once normalised. */
export const isValidAwb = (typed: string): boolean => /^[A-Z0-9]{4,30}$/.test(normaliseAwb(typed));

export const AWB_PLACEHOLDER = "{awb}";

/** The server's template rules, as a message; "" when the template is fine or empty. */
export const templateProblem = (template: string): string => {
  const t = (template || "").trim();
  if (!t) return "";
  if (t.length > 300) return "Keep the tracking link under 300 characters";
  if (!/^https:\/\//i.test(t)) return "The tracking link must start with https://";
  const first = t.indexOf(AWB_PLACEHOLDER);
  if (first < 0 || t.indexOf(AWB_PLACEHOLDER, first + 1) >= 0) return "Paste the tracking page of this AWB, or put {awb} where the number goes";
  try {
    const url = new URL(t.replace(AWB_PLACEHOLDER, "AWB"));
    if (!url.hostname) return "Check the tracking link";
  } catch {
    return "Check the tracking link";
  }
  return "";
};

/** The courier page for one AWB, or null when the template or the AWB is not usable. */
export const linkFromTemplate = (template: string | null | undefined, awb: string): string | null => {
  if (!template || templateProblem(template) || !isValidAwb(awb)) return null;
  return template.trim().replace(AWB_PLACEHOLDER, encodeURIComponent(normaliseAwb(awb)));
};

/**
 * A pasted tracking page for this AWB becomes a template: the AWB (as typed, or in its compact form) is swapped
 * for {awb}, so the next parcel gets its own link without anyone knowing about placeholders. A link that already
 * has {awb}, or does not contain the AWB, comes back trimmed and unchanged.
 */
export const templateFromPastedUrl = (pasted: string, awb: string): string => {
  const url = (pasted || "").trim();
  if (!url || url.includes(AWB_PLACEHOLDER)) return url;
  const candidates = Array.from(new Set([(awb || "").trim(), normaliseAwb(awb)])).filter((c) => c.length >= 4);
  for (const candidate of candidates) {
    for (const form of [candidate, encodeURIComponent(candidate)]) {
      const at = url.toLowerCase().indexOf(form.toLowerCase());
      if (at >= 0) return url.slice(0, at) + AWB_PLACEHOLDER + url.slice(at + form.length);
    }
  }
  return url;
};

/** A slip photo the server accepts, by the browser's file type (or name when the type is blank, as on some phones). */
export const slipFileType = (file: { type?: string; name?: string }): SlipFileType | null => {
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  if (type === "image/jpeg" || type === "image/jpg" || (!type && /\.jpe?g$/.test(name))) return "JPG";
  if (type === "image/png" || (!type && name.endsWith(".png"))) return "PNG";
  if (type === "image/heic" || type === "image/heif" || /\.hei[cf]$/.test(name)) return "HEIC";
  return null;
};

export const SLIP_MAX_BYTES = 10 * 1024 * 1024;

/**
 * The customer message, kept in one place so a Hindi version is one more entry later. Never a price, a photo
 * link or anything internal: only the order number, studio, courier, AWB and the courier's own page.
 */
export const SHARE_TEXT = {
  opening: (order: string, studio: string) => `Your order ${order}${studio ? ` from ${studio}` : ""} has been sent.`,
  parcel: (courier: string, awb: string, link: string | null) =>
    `Courier: ${courier}. AWB: ${awb}.${link ? ` Track it here: ${link}` : ""}`,
};

export type ShareParcel = Pick<ParcelView, "courierName" | "awbNumber" | "trackingUrl">;

/** One parcel: one paragraph. Several: the opening, then one line per parcel. */
export const shareText = (parcels: ShareParcel[], order: string, studio: string): string => {
  const lines = parcels.map((p) => SHARE_TEXT.parcel(p.courierName || "courier", p.awbNumber, p.trackingUrl || null));
  const opening = SHARE_TEXT.opening(order || "", (studio || "").trim());
  return parcels.length <= 1 ? [opening, ...lines].join(" ") : [opening, ...lines].join("\n");
};

/** A wa.me link to the party when the memo has a phone; null means share another way (share sheet or copy). */
export const whatsAppShareHref = (partyPhone: string | null | undefined, text: string): string | null => {
  const digits = waDigits(partyPhone);
  return digits.length >= 10 ? whatsAppHref(digits, text) : null;
};
